import { useContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { SearchFiltersActionTypes } from '../../../../reducers/search-filters/actions';
import { SearchFiltersTypes } from '../../../../reducers/search-filters/reducer';
import ExplorePropertiesPresentation from './ExplorePropertiesPresentation';
import MExplorePropertiesPresentation from './mobile/ExplorePropertiesPresentation';
import {
  SearchContext,
  SearchContextInterface
} from '../../../search-new/context/SearchContext';

type EntityDetails = {
  type:
    | SearchFiltersTypes.COMPOUNDS
    | SearchFiltersTypes.AREAS
    | SearchFiltersTypes.DEVELOPERS;
  id: number;
  name: string;
  currency?: string;
  resale_min_price?: number;
};

type ExploreContainerProps = {
  headline?: string;
  entityDetails?: EntityDetails;
  filterOptions: any;
  exploreLocation: string;
};

export type ExplorePresentationProps = {
  headline?: string;
  entityDetails?: EntityDetails;
  pageNumber: number;
  setPageNumber: (pageNumber: PageNumber) => void;
  changePageHandler?: (pageNumber: number) => void;
  loadMoreHandler?: (pageNumber: number) => void;
  explorePropertiesRef: React.RefObject<HTMLDivElement>;
  exploreLocation: string;
  compoundLaunches?: {}[];
  activeTab: 'all' | 'developer_sale' | 'resale';

  saleTypes: {
    name: 'all' | 'developer_sale' | 'resale';
    value: 'all' | 'developer_sale' | 'resale';
  }[];
  handleTabChange: (saleType: 'all' | 'developer_sale' | 'resale') => void;
  appliedFilters?: number;
};

type PageNumber = {
  page: number;
  start: number;
};

export const saleTypes: any = [
  {
    name: 'all',
    value: 'all'
  },
  {
    name: 'developer_sale',
    value: 'developer_sale'
  },
  {
    name: 'resale',
    value: 'resale'
  }
];

const ExplorePropertiesContainer = ({
  headline,
  entityDetails,
  exploreLocation
}: ExploreContainerProps) => {
  const router = useRouter();
  const { query } = router;
  const isMobile = useSelector((state: any) => state.app.isMobile);
  const { searchFiltersState, searchFiltersDispatch, appliedFiltersCount } =
    useContext(SearchContext) as SearchContextInterface;
  const explorePropertiesRef = useRef<HTMLDivElement>(null);
  const prevCategoryFilter = useRef<string>();
  const prevSaleTypeFilter = useRef<string[]>();
  const [activeTab, setActiveTab] = useState<
    'all' | 'developer_sale' | 'resale'
  >('all');

  const [pageNumber, setPageNumber] = useState<PageNumber>({
    page: 1,
    start: 1
  });

  const resetPageNumber = () => {
    searchFiltersDispatch({
      type: SearchFiltersActionTypes.UPDATE_PAGE_NUMBER,
      payload: {
        name: 'page_number',
        value: 1
      }
    });
    setPageNumber({
      page: 1,
      start: 1
    });
  };

  // set the default tab if filter already exists
  useEffect(() => {
    if (searchFiltersState.searchFilters.sale_type.length == 0) {
      setActiveTab('all');
    } else if (
      searchFiltersState.searchFilters.sale_type.length == 1 &&
      searchFiltersState.searchFilters.sale_type.includes('resale')
    ) {
      setActiveTab('resale');
    } else if (
      searchFiltersState.searchFilters.sale_type.includes('developer_sale')
    )
      setActiveTab('developer_sale');
  }, [searchFiltersState.searchFilters]);

  /* set the category type by default always to properties in case of compounds and keep the old state */
  useEffect(() => {
    if (entityDetails?.type !== SearchFiltersTypes.COMPOUNDS) return;
    if (!prevCategoryFilter.current)
      prevCategoryFilter.current = searchFiltersState.searchFilters.category;
    searchFiltersDispatch({
      type: SearchFiltersActionTypes.UPDATE_FILTERS_WITH_SINGLE_VALUE,
      payload: {
        name: SearchFiltersTypes.CATEGORY,
        value: 'property'
      }
    });
    return () => {
      searchFiltersDispatch({
        type: SearchFiltersActionTypes.UPDATE_FILTERS_WITH_SINGLE_VALUE,
        payload: {
          name: SearchFiltersTypes.CATEGORY,
          value: prevCategoryFilter.current
        }
      });
    };
  }, []);

  useEffect(() => {
    if (entityDetails?.type !== SearchFiltersTypes.COMPOUNDS) return;
    if (!prevSaleTypeFilter.current)
      prevSaleTypeFilter.current = searchFiltersState.searchFilters.sale_type;
    searchFiltersDispatch({
      type: SearchFiltersActionTypes.UPDATE_FILTERS_WITH_MULTISELECT,
      payload: {
        name: SearchFiltersTypes.SALE_TYPE,
        value: []
      }
    });
    return () => {
      searchFiltersDispatch({
        type: SearchFiltersActionTypes.UPDATE_FILTERS_WITH_MULTISELECT,
        payload: {
          name: SearchFiltersTypes.SALE_TYPE,
          value: prevSaleTypeFilter.current as any
        }
      });
    };
  }, [router.asPath]);

  useEffect(() => {
    searchFiltersDispatch({
      type: SearchFiltersActionTypes.UPDATE_SORTING_ORDER,
      payload: {
        order_by: '',
        order_direction: ''
      }
    });
    return () => {
      searchFiltersDispatch({
        type: SearchFiltersActionTypes.UPDATE_FILTERS_WITH_SINGLE_VALUE,
        payload: {
          name: SearchFiltersTypes.DELIVERY_DATE,
          value: undefined
        }
      });
    };
  }, [router.asPath]);

  useEffect(() => {
    if (entityDetails)
      searchFiltersDispatch({
        type: SearchFiltersActionTypes.UPDATE_FILTERS_WITH_MULTISELECT,
        payload: {
          name: entityDetails.type,
          value: [entityDetails.id]
        }
      });

    return () => {
      // Perform the action on unmount
      if (entityDetails)
        searchFiltersDispatch({
          type: SearchFiltersActionTypes.UPDATE_FILTERS_WITH_MULTISELECT,
          payload: {
            name: entityDetails.type,
            value: []
          }
        });
    };
  }, [query]);

  useEffect(() => {
    setPageNumber({
      page: 1,
      start: 1
    });
  }, [searchFiltersState.searchFilters]);

  const handleTabChange = (saleType: 'all' | 'developer_sale' | 'resale') => {
    resetPageNumber();

    searchFiltersDispatch({
      type: SearchFiltersActionTypes.UPDATE_FILTERS_WITH_MULTISELECT,
      payload: {
        name: SearchFiltersTypes.SALE_TYPE,
        value: saleType === 'all' ? [] : [saleType]
      }
    });
  };

  const changePageHandler = (pageNumber: number) => {
    setPageNumber({
      page: pageNumber,
      start: (pageNumber - 1) * 12 + 1
    });
  };

  const loadMoreHandler = (pageNumber: number) => {
    setPageNumber({
      page: pageNumber,
      start: (pageNumber - 1) * 12 + 1
    });
  };

  return isMobile ? (
    <MExplorePropertiesPresentation
      entityDetails={entityDetails}
      loadMoreHandler={loadMoreHandler}
      explorePropertiesRef={explorePropertiesRef}
      exploreLocation={exploreLocation}
      saleTypes={saleTypes}
      handleTabChange={handleTabChange}
      pageNumber={pageNumber.page}
      setPageNumber={setPageNumber}
      headline={headline}
      activeTab={activeTab}
      appliedFilters={appliedFiltersCount}
    />
  ) : (
    <ExplorePropertiesPresentation
      headline={headline}
      entityDetails={entityDetails}
      changePageHandler={changePageHandler}
      explorePropertiesRef={explorePropertiesRef}
      exploreLocation={exploreLocation}
      saleTypes={saleTypes}
      handleTabChange={handleTabChange}
      appliedFilters={appliedFiltersCount}
      activeTab={activeTab}
      pageNumber={pageNumber.page}
      setPageNumber={setPageNumber}
    />
  );
};

export default ExplorePropertiesContainer;
